import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import MainLayout from '../../../../../components/layout/MainLayout';
import { getBackofficeSidebarItems } from '../../../shell/backofficeSidebar';
import { useAuth } from '../../../../../context/AuthContext';
import { PaginatedListLoading } from '../../../../../components/ui/PaginatedListLoading';
import { useCurrency } from '../../../../../context/CurrencyContext';
import { resolveApiErrorMessage } from '../../../../../utils/apiErrorMessage';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3001';

type BudgetPeriodRow = {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  initialAmount: number;
  currency: string;
  status: string;
  balance?: number;
  overBudget?: boolean;
  createdBy?: { id: string; employeeProfile?: { fullName?: string | null } | null } | null;
  entries?: {
    id: string;
    type: string;
    amount: number;
    description?: string | null;
    createdAt: string;
    createdBy?: { id: string; employeeProfile?: { fullName?: string | null } | null } | null;
  }[];
};

function BudgetPage() {
  const { authorizedFetch } = useAuth();
  const { currency, formatPriceFrom } = useCurrency();
  const { t } = useTranslation();
  const location = useLocation();
  const [workspaceScope, setWorkspaceScope] = useState<string>(() => {
    if (typeof window === 'undefined') return '__all__';
    return window.localStorage.getItem('workspace_department_scope') || '__all__';
  });
  const [periods, setPeriods] = useState<BudgetPeriodRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<BudgetPeriodRow | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', startDate: '', endDate: '', initialAmount: '', currency: 'USD' });
  const [saving, setSaving] = useState(false);
  const [adjustmentAmount, setAdjustmentAmount] = useState('');
  const [adjustmentDesc, setAdjustmentDesc] = useState('');
  const [expenseAmount, setExpenseAmount] = useState('');
  const [expenseDesc, setExpenseDesc] = useState('');
  const activeCount = periods.filter((p) => p.status === 'ACTIVE').length;
  const overBudgetCount = periods.filter((p) => !!p.overBudget).length;

  const isAllScope = useMemo(() => {
    const scope = String(workspaceScope || '').trim().toLowerCase();
    return !scope || scope === '__all__';
  }, [workspaceScope]);

  useEffect(() => {
    const readScope = () =>
      (typeof window !== 'undefined'
        ? window.localStorage.getItem('workspace_department_scope')
        : null) || '__all__';
    const handleScopeChange = () => {
      setWorkspaceScope(readScope());
    };
    window.addEventListener(
      'workspace-department-scope:change',
      handleScopeChange as EventListener,
    );
    window.addEventListener('storage', handleScopeChange);
    return () => {
      window.removeEventListener(
        'workspace-department-scope:change',
        handleScopeChange as EventListener,
      );
      window.removeEventListener('storage', handleScopeChange);
    };
  }, []);

  useEffect(() => {
    if (isAllScope && showForm) {
      setShowForm(false);
    }
  }, [isAllScope, showForm]);

  const loadPeriods = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authorizedFetch(`${API_BASE}/budget/periods?page=${page}&pageSize=10`);
      if (!res.ok) {
        throw new Error(await resolveApiErrorMessage(res, 'Failed to load budget periods'));
      }
      const data = await res.json();
      setPeriods(data.items);
      setTotal(data.total);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [authorizedFetch, page]);

  useEffect(() => {
    void loadPeriods();
  }, [loadPeriods]);

  const loadOne = async (id: string): Promise<BudgetPeriodRow | null> => {
    try {
      const res = await authorizedFetch(`${API_BASE}/budget/periods/${id}`);
      if (!res.ok) return null;
      const p = await res.json();
      setSelectedPeriod(p);
      return p;
    } catch {
      setSelectedPeriod(null);
      return null;
    }
  };

  const handleCreatePeriod = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const res = await authorizedFetch(`${API_BASE}/budget/periods`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          startDate: form.startDate,
          endDate: form.endDate,
          initialAmount: Number(form.initialAmount) || 0,
          currency: form.currency,
        }),
      });
      if (!res.ok) {
        throw new Error(await resolveApiErrorMessage(res, 'Failed to create period'));
      }
      setShowForm(false);
      setForm({ name: '', startDate: '', endDate: '', initialAmount: '', currency: 'USD' });
      loadPeriods();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed');
    } finally {
      setSaving(false);
    }
  };

  const handleAddAdjustment = async (periodId: string) => {
    const amount = Number(adjustmentAmount);
    if (Number.isNaN(amount)) return;
    setSaving(true);
    try {
      const res = await authorizedFetch(`${API_BASE}/budget/periods/${periodId}/adjustment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount, description: adjustmentDesc || undefined }),
      });
      if (!res.ok) {
        throw new Error(await resolveApiErrorMessage(res, 'Failed to add adjustment'));
      }
      setAdjustmentAmount('');
      setAdjustmentDesc('');
      if (selectedPeriod?.id === periodId) loadOne(periodId);
      loadPeriods();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed');
    } finally {
      setSaving(false);
    }
  };

  const handleAddExpense = async (periodId: string) => {
    const amount = Number(expenseAmount);
    if (Number.isNaN(amount) || amount <= 0) return;
    setSaving(true);
    try {
      const res = await authorizedFetch(`${API_BASE}/budget/periods/${periodId}/expense`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount, description: expenseDesc || 'Other expense' }),
      });
      if (!res.ok) {
        throw new Error(await resolveApiErrorMessage(res, 'Failed to add expense'));
      }
      setExpenseAmount('');
      setExpenseDesc('');
      if (selectedPeriod?.id === periodId) loadOne(periodId);
      loadPeriods();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed');
    } finally {
      setSaving(false);
    }
  };

  const handleSetStatus = async (periodId: string, status: string) => {
    setSaving(true);
    try {
      const res = await authorizedFetch(`${API_BASE}/budget/periods/${periodId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) {
        throw new Error(await resolveApiErrorMessage(res, 'Failed to update period'));
      }
      if (selectedPeriod?.id === periodId) loadOne(periodId);
      loadPeriods();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed');
    } finally {
      setSaving(false);
    }
  };

  void total;
  void setPage;

  return (
    <MainLayout withSidebar sidebarItems={getBackofficeSidebarItems(t)} activePath={location.pathname}>
      <main className="workspace-page">
        <section>
          <h1 className="workspace-page-title">
            {t('backoffice.budget_title', 'Budget')}
          </h1>
          <p className="workspace-page-subtitle text-slate-600">
            {t('backoffice.budget_subtitle', 'Set budget periods. Purchases, losses and other expenses are deducted automatically.')}
          </p>
        </section>

        {error && <p className="text-xs text-red-500">{error}</p>}

        <section className="workspace-panel">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <div className="workspace-stat-card">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Total periods</div>
              <div className="mt-1 text-xl font-semibold text-slate-900">{periods.length}</div>
            </div>
            <div className="workspace-stat-card">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Active periods</div>
              <div className="mt-1 text-xl font-semibold text-emerald-700">{activeCount}</div>
            </div>
            <div className="workspace-stat-card">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Over budget</div>
              <div className="mt-1 text-xl font-semibold text-rose-700">{overBudgetCount}</div>
            </div>
          </div>
        </section>

        <section className="workspace-panel">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-slate-900">{t('backoffice.budget_periods', 'Budget periods')}</h2>
            <button
              type="button"
              onClick={() => {
                if (isAllScope) return;
                setShowForm((x) => !x);
              }}
              disabled={isAllScope}
              title={
                isAllScope
                  ? t(
                      'backoffice.budget_new_scope_hint_title',
                      'Select a specific department scope to create a budget period.',
                    )
                  : undefined
              }
              className="rounded-full bg-brand-600 px-4 py-2 text-xs font-semibold text-white shadow-sm hover:bg-brand-500 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {showForm ? t('common.cancel') : t('backoffice.budget_new', 'New period')}
            </button>
          </div>

          {isAllScope && !showForm ? (
            <p className="mb-4 text-xs font-medium text-amber-700">
              {t(
                'backoffice.budget_new_scope_hint',
                'Select a specific department scope to add a budget period.',
              )}
            </p>
          ) : null}

          {showForm && (
            <form onSubmit={handleCreatePeriod} className="mb-6 grid gap-3 border-b border-slate-100 pb-6 sm:grid-cols-2">
              <input
                type="text"
                required
                placeholder={t('backoffice.budget_name', 'Period name')}
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
              />
              <label className="text-xs font-medium text-slate-700">
                {t('backoffice.budget_from_date', 'From')}
                <input
                  type="date"
                  required
                  value={form.startDate}
                  onChange={(e) => setForm((f) => ({ ...f, startDate: e.target.value }))}
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
              </label>
              <label className="text-xs font-medium text-slate-700">
                {t('backoffice.budget_to_date', 'To')}
                <input
                  type="date"
                  required
                  value={form.endDate}
                  onChange={(e) => setForm((f) => ({ ...f, endDate: e.target.value }))}
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                placeholder={t('backoffice.budget_initial', 'Initial amount')}
                value={form.initialAmount}
                onChange={(e) => setForm((f) => ({ ...f, initialAmount: e.target.value }))}
                className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
              />
              <button type="submit" disabled={saving} className="rounded-md bg-brand-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
                {saving ? t('common.saving') : t('backoffice.budget_create', 'Create')}
              </button>
            </form>
          )}

          {loading ? (
            <PaginatedListLoading
              title={t('backoffice.budget_loading', 'Loading…')}
              skeletonRows={3}
            />
          ) : periods.length === 0 ? (
            <p className="text-sm text-slate-600">{t('backoffice.budget_empty', 'No budget periods yet.')}</p>
          ) : (
            <div className="space-y-3">
              {periods.map((p) => (
                <div
                  key={p.id}
                  className={`rounded-lg border p-4 ${p.overBudget ? 'border-red-200 bg-red-50/50' : 'border-slate-200 bg-white'}`}
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <span className="font-medium text-slate-900">{p.name}</span>
                      <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">{p.status}</span>
                      {p.overBudget && (
                        <span className="ml-2 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
                          {t('backoffice.budget_overbudget', 'Over budget')}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-shrink-0 flex-col items-end gap-2 text-right">
                      <div className="text-sm text-slate-700">
                        {new Date(p.startDate).toLocaleDateString()} – {new Date(p.endDate).toLocaleDateString()} ·{' '}
                        <span className="font-bold text-blue-600">
                          {currency} {typeof p.balance === 'number' ? formatPriceFrom(p.balance, p.currency) : '—'}
                        </span>
                      </div>
                      {p.status === 'ACTIVE' && (
                        <button
                          type="button"
                          onClick={() => handleSetStatus(p.id, 'CLOSED')}
                          className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-800 shadow-sm hover:border-slate-400 hover:bg-slate-50"
                        >
                          {t('backoffice.budget_close', 'Close')}
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => (selectedPeriod?.id === p.id ? setSelectedPeriod(null) : void loadOne(p.id))}
                      className="text-xs font-medium text-brand-600 hover:underline"
                    >
                      {selectedPeriod?.id === p.id ? t('backoffice.budget_hide_detail', 'Hide detail') : t('backoffice.budget_view_detail', 'View detail')}
                    </button>
                    {p.status === 'DRAFT' && (
                      <button
                        type="button"
                        onClick={() => handleSetStatus(p.id, 'ACTIVE')}
                        className="text-xs font-medium text-green-600 hover:underline"
                      >
                        {t('backoffice.budget_activate', 'Activate')}
                      </button>
                    )}
                  </div>
                  {selectedPeriod?.id === p.id && selectedPeriod.entries && (
                    <div className="mt-4 border-t border-slate-100 pt-4">
                      <p className="mb-2 text-xs font-semibold text-slate-500 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                        <span className="font-bold text-blue-600">
                          {t('backoffice.budget_balance', 'Balance')}: {formatPriceFrom(selectedPeriod.balance ?? 0, selectedPeriod.currency)}
                        </span>
                        {selectedPeriod.createdBy && (
                          <span className="text-[11px] text-slate-500">
                            {t('backoffice.budget_created_by', 'Created by')}{' '}
                            <span className="font-medium text-slate-700">
                              {selectedPeriod.createdBy.employeeProfile?.fullName ?? t('backoffice.budget_employee_unknown', 'Unknown employee')}
                            </span>{' '}
                            <span className="ml-1 text-slate-400">
                              ({t('backoffice.budget_employee_id', 'ID')}: {selectedPeriod.createdBy.id})
                            </span>
                          </span>
                        )}
                      </p>
                      <div className="mb-3 flex flex-wrap gap-2">
                        <input
                          type="number"
                          step="0.01"
                          placeholder={t('backoffice.budget_adjustment', 'Adjustment (+/-)')}
                          value={adjustmentAmount}
                          onChange={(e) => setAdjustmentAmount(e.target.value)}
                          className="w-28 rounded border border-slate-300 px-2 py-1 text-xs"
                        />
                        <input
                          type="text"
                          placeholder={t('backoffice.budget_description', 'Description')}
                          value={adjustmentDesc}
                          onChange={(e) => setAdjustmentDesc(e.target.value)}
                          className="w-40 rounded border border-slate-300 px-2 py-1 text-xs"
                        />
                        <button
                          type="button"
                          onClick={() => handleAddAdjustment(p.id)}
                          disabled={saving || adjustmentAmount === ''}
                          className="rounded bg-slate-700 px-2 py-1 text-xs text-white disabled:opacity-50"
                        >
                          {t('backoffice.budget_add_adjustment', 'Add adjustment')}
                        </button>
                      </div>
                      <div className="mb-3 flex flex-wrap gap-2">
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder={t('backoffice.budget_expense_amount', 'Expense amount')}
                          value={expenseAmount}
                          onChange={(e) => setExpenseAmount(e.target.value)}
                          className="w-28 rounded border border-slate-300 px-2 py-1 text-xs"
                        />
                        <input
                          type="text"
                          placeholder={t('backoffice.budget_expense_desc', 'e.g. Servers, ads')}
                          value={expenseDesc}
                          onChange={(e) => setExpenseDesc(e.target.value)}
                          className="w-40 rounded border border-slate-300 px-2 py-1 text-xs"
                        />
                        <button
                          type="button"
                          onClick={() => handleAddExpense(p.id)}
                          disabled={saving || expenseAmount === ''}
                          className="rounded bg-slate-700 px-2 py-1 text-xs text-white disabled:opacity-50"
                        >
                          {t('backoffice.budget_add_expense', 'Add expense')}
                        </button>
                      </div>
                      <table className="min-w-full text-xs text-slate-700">
                        <thead>
                          <tr className="border-b border-slate-200 text-slate-500">
                            <th className="py-1 pr-2 text-left font-bold">{t('backoffice.budget_type', 'Type')}</th>
                            <th className="py-1 pr-2 text-right font-bold">{t('backoffice.budget_amount', 'Amount')}</th>
                            <th className="py-1 pr-2 text-left font-bold">{t('backoffice.budget_description', 'Description')}</th>
                            <th className="py-1 pr-2 text-left font-bold">{t('backoffice.budget_date', 'Date')}</th>
                            <th className="py-1 pr-2 text-left font-bold">{t('backoffice.budget_employee', 'Employee')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedPeriod.entries.map((e) => {
                            const amount = Number(e.amount);
                            const isPositive = amount > 0;
                            const amountClass = isPositive ? 'font-bold text-green-600' : 'font-bold text-red-600';
                            const entryTypeKey = `admin.budget_entry_type_${e.type.toLowerCase()}`;
                            return (
                              <tr key={e.id} className="border-b border-slate-50">
                                <td className="py-1 pr-2 font-bold">{t(entryTypeKey, e.type)}</td>
                                <td className={`py-1 pr-2 text-right ${amountClass}`}>{selectedPeriod ? formatPriceFrom(amount, selectedPeriod.currency) : String(amount)}</td>
                                <td className="py-1 pr-2 font-bold">{e.description ?? '—'}</td>
                                <td className="py-1 pr-2 font-bold">{new Date(e.createdAt).toLocaleString()}</td>
                                <td className="py-1 pr-2">
                                  {e.createdBy ? (
                                    <div className="flex flex-col">
                                      <span className="text-[11px] font-semibold text-slate-700">
                                        {e.createdBy.employeeProfile?.fullName ?? t('backoffice.budget_employee_unknown', 'Unknown employee')}
                                      </span>
                                      <span className="text-[10px] text-slate-400">
                                        {t('backoffice.budget_employee_id', 'ID')}: {e.createdBy.id}
                                      </span>
                                    </div>
                                  ) : (
                                    <span className="text-[11px] text-slate-400">
                                      {t('backoffice.budget_employee_unknown', 'Unknown employee')}
                                    </span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        {selectedPeriod && (
          <section className="workspace-panel">
            <p className="text-xs text-slate-600">
              {t(
                'admin.budget_report_moved_hint',
                'Detailed reports for this budget period are available in the “Stats & reports” section.',
              )}
            </p>
          </section>
        )}
      </main>
    </MainLayout>
  );
}

export default BudgetPage;

